/*
 Copyright (c), CKSource Holding sp. z o.o. All rights reserved.
 */

import { EmitterMixin } from 'ckeditor5/src/utils.js';

import MessagesCompressor from './../messagescompressor';

import AddSuggestionMessage from './messages/addsuggestionmessage';
import AddSuggestionResponse from './responses/addsuggestionresponse';
import GetSuggestionMessage from './messages/getsuggestionmessage';
import GetSuggestionResponse from './responses/getsuggestionresponse';
import GetAllSuggestionsMessage from './messages/getallsuggestionsmessage';
import GetAllSuggestionsResponse from './responses/getallsuggestionsresponse';
import UpdateSuggestionMessage from './messages/updatesuggestionmessage';
import ConnectMessage from './messages/connectmessage';
import ConnectResponse from './responses/connectresponse';

import CKEditorCloudServicesError from '../ckeditorcloudserviceserror';
import WebSocketGateway, {
	IWebSocketGateway,
	WebSocketGatewayState,
	WEB_SOCKET_GATEWAY_STATES
} from '../websocketgateway/websocketgateway';
import { ISuggestion } from './descriptors/suggestiondescriptor';
import Channel from '../websocketgateway/channel';
import ServiceNotConnectedError from '../errors/ServiceNotConnectedError';

import type Message from '../message';
import CKEditorCloudServicesServerError from '../ckeditorcloudservicesservererror';
import BatchUpdateSuggestionsStateMessage from './messages/batchUpdateSuggestionsStateMessage';

// eslint-disable-next-line @typescript-eslint/naming-convention
export const _SERVICE: number = 10;

interface ISuggestionUpdateProps {
	hasComments?: boolean;

	state?: string;

	attributes?: Record<string, unknown>;
}

export interface IBatchUpdateSuggestionsStateParams {
	ids: string[];
	state: string;
}

// eslint-disable-next-line new-cap
class TrackChangesService extends /* #__PURE__ -- @preserve */ EmitterMixin() {
	private _isConnected: boolean = false;

	private _wsGateway?: IWebSocketGateway;

	private _channel?: Channel;

	private static readonly _SERVICE: number = _SERVICE;

	public constructor( private readonly _documentId: string ) {
		super();
	}

	public get isConnected(): boolean {
		return this._isConnected;
	}

	/**
	 * Connects to WebSocketGateway and starts listening on channel.
	 */
	public async connect( wsGateway: IWebSocketGateway ): Promise<ISuggestion[] | void> {
		if ( this._isConnected ) {
			return;
		}

		if ( wsGateway.state !== WEB_SOCKET_GATEWAY_STATES.CONNECTED ) {
			throw new CKEditorCloudServicesError( 'WebSocket Gateway is not connected.', wsGateway );
		}

		const connectMessage: ConnectMessage = new ConnectMessage( this._documentId );

		this._wsGateway = wsGateway;
		this.stopListening( wsGateway, 'change:state' );

		const serviceResponse: Buffer = await wsGateway._sendRequest(
			_SERVICE,
			ConnectMessage.TYPE,
			MessagesCompressor.encode( connectMessage )
		);

		const connectResponse: ConnectResponse = MessagesCompressor.decode(
			serviceResponse,
			ConnectResponse
		) as ConnectResponse;

		this.listenTo(
			wsGateway,
			'change:state',
			// eslint-disable-next-line @typescript-eslint/naming-convention
			( _0, _1, value ) => this._onWsGatewayStateChange( value as WebSocketGatewayState ),
			{ priority: WebSocketGateway._CHANGE_STATE_EVENT_PRIORITY }
		);

		this._connectToChannel( wsGateway, connectResponse.channel );

		this._isConnected = true;
		this.fire( 'connected' );

		return connectResponse.suggestions;
	}

	/**
	 * Disconnects TrackChangesService from CKEditor Cloud Services.
	 */
	public disconnect(): void {
		if ( !this._isConnected ) {
			return;
		}

		this._isConnected = false;

		if ( this._wsGateway ) {
			this.stopListening( this._wsGateway );

			this._wsGateway = undefined;
		}

		if ( this._channel ) {
			this.stopListening( this._channel );

			this._channel = undefined;
		}

		this.fire( 'disconnected' );

		this.stopListening();
	}

	/**
	 * Adds suggestion and returns AddSuggestionResponse.
	 */
	public async add(
		id: string,
		type: string,
		data: Record<string, unknown>,
		originalSuggestionId: string | null = null,
		attributes?: Record<string, unknown>
	): Promise<AddSuggestionResponse> {
		const message: AddSuggestionMessage = new AddSuggestionMessage(
			id,
			this._documentId,
			type,
			data,
			originalSuggestionId,
			attributes
		);

		const responseBuffer: Buffer = await this._sendRequest(
			AddSuggestionMessage.TYPE,
			message
		);

		return MessagesCompressor.decode( responseBuffer, AddSuggestionResponse ) as AddSuggestionResponse;
	}

	/**
	 * Returns suggestion with given id.
	 */
	public async get( id: string, attempt: number = 1 ): Promise<GetSuggestionResponse> {
		const message: GetSuggestionMessage = new GetSuggestionMessage( id, this._documentId );

		try {
			const responseBuffer: Buffer = await this._sendRequest( GetSuggestionMessage.TYPE, message );

			return MessagesCompressor.decode( responseBuffer, GetSuggestionResponse ) as GetSuggestionResponse;
		} catch ( error ) {
			if ( error.name === 'CKEditorCloudServicesServerError' && error.code === '404' && attempt < 5 ) {
				await _wait( 100 * attempt );

				return this.get( id, attempt + 1 );
			}

			if ( error.name === 'CKEditorCloudServicesServerError' ) {
				throw error;
			}

			throw CKEditorCloudServicesServerError.fromPublicError( error );
		}
	}

	/**
	 * Returns suggestions from given documentId.
	 */
	public async getAll(): Promise<ISuggestion[]> {
		const message: GetAllSuggestionsMessage = new GetAllSuggestionsMessage( this._documentId );
		const responseBuffer: Buffer = await this._sendRequest( GetAllSuggestionsMessage.TYPE, message );

		const { suggestions } = MessagesCompressor.decode( responseBuffer, GetAllSuggestionsResponse ) as GetAllSuggestionsResponse;

		return suggestions;
	}

	/**
	 * Updates suggestion.
	 */
	public async update( id: string, props: ISuggestionUpdateProps = {} ): Promise<void> {
		const { hasComments, state, attributes } = props;

		const isHasCommentsUpdated: boolean = hasComments !== undefined;

		const message: UpdateSuggestionMessage = new UpdateSuggestionMessage(
			id,
			this._documentId,
			hasComments,
			isHasCommentsUpdated,
			state,
			attributes
		);

		await this._sendRequest( UpdateSuggestionMessage.TYPE, message );
	}

	public async batchUpdateState( params: IBatchUpdateSuggestionsStateParams ): Promise<void> {
		const message: BatchUpdateSuggestionsStateMessage = new BatchUpdateSuggestionsStateMessage(
			params.ids,
			this._documentId,
			params.state
		);

		await this._sendRequest( BatchUpdateSuggestionsStateMessage.TYPE, message );
	}

	/**
	 * Starts to listen on a given channel.
	 */
	private _connectToChannel( wsGateway: IWebSocketGateway, channelName: string ): void {
		this._channel = wsGateway._getChannel( TrackChangesService._SERVICE, channelName );

		if ( !this._channel ) {
			return;
		}

		this.listenTo(
			this._channel,
			this._channel.getEventName( UpdateSuggestionMessage.TYPE ),
			( event, data ) => {
				const updateSuggestionMessage: UpdateSuggestionMessage = MessagesCompressor.decode(
					data,
					UpdateSuggestionMessage
				) as UpdateSuggestionMessage;

				this.fire( 'suggestionUpdated', updateSuggestionMessage );
			}
		);
	}

	/**
	 * Processes websocket gateway state changes.
	 */
	private _onWsGatewayStateChange( value: WebSocketGatewayState ): void {
		if ( value === WEB_SOCKET_GATEWAY_STATES.DISCONNECTED ) {
			this.disconnect();
		}
	}

	private _sendRequest( method: string, message: Message ): Promise<Buffer> {
		if ( !this._wsGateway || !this._isConnected ) {
			throw new ServiceNotConnectedError( 'Track Changes', this );
		}

		return this._wsGateway._sendRequest( _SERVICE, method, MessagesCompressor.encode( message ) );
	}
}

function _wait( time: number ): Promise<void> {
	return new Promise( resolve => {
		setTimeout( resolve, time );
	} );
}

export default TrackChangesService;
